import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, Sun } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DailySpecialPage() {
  const [special, setSpecial] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', price: '', menu_item_id: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = async () => {
    try {
      const [specialRes, menuRes] = await Promise.all([
        fetch(`${API}/business/daily-special`, { headers }),
        fetch(`${API}/business/menu`, { headers })
      ]);
      if (specialRes.ok) {
        const s = await specialRes.json();
        if (s) {
          setSpecial(s);
          setForm({ name: s.name || '', description: s.description || '', price: s.price?.toString() || '', menu_item_id: s.menu_item_id || '' });
        }
      }
      if (menuRes.ok) setMenuItems(await menuRes.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const selectMenuItem = (item) => {
    setForm({ name: item.name, description: item.description || '', price: item.price?.toString() || '', menu_item_id: item.id });
  };

  const saveSpecial = async () => {
    if (!form.name) return toast.error("Nom requis");
    setSaving(true);
    try {
      const body = {
        name: form.name,
        description: form.description,
        price: form.price ? parseFloat(form.price) : null,
        menu_item_id: form.menu_item_id || null
      };
      const res = await fetch(`${API}/business/daily-special`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success("Plat du jour publié !");
      fetchData();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-white border-b px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/business/dashboard"><Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button></Link>
          <div>
            <h1 className="text-xl font-bold" data-testid="daily-special-page-title">Plat du jour</h1>
            <p className="text-sm text-muted-foreground">
              {special ? "Publié aujourd'hui" : "Pas encore publié aujourd'hui"}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Quick select from menu */}
        {menuItems.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Sun className="w-4 h-4 text-amber-500" />
                Sélectionner depuis le menu
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {menuItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => selectMenuItem(item)}
                    className={`p-2 rounded-lg text-left text-sm transition-colors ${
                      form.menu_item_id === item.id ? 'bg-primary/10 border-2 border-primary/30' : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                    data-testid={`select-menu-${item.id}`}
                  >
                    <p className="font-medium truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.price?.toFixed(2)}€</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Manual input */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <h3 className="font-semibold">Détails du plat du jour</h3>
            <div>
              <Label>Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Blanquette de veau" data-testid="ds-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Servi avec riz basmati et légumes de saison" />
            </div>
            <div>
              <Label>Prix (€)</Label>
              <Input type="number" min="0" step="0.5" value={form.price} onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))} placeholder="14.90" data-testid="ds-price" />
            </div>
            <Button onClick={saveSpecial} disabled={saving} className="w-full" data-testid="save-daily-special-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {special ? "Mettre à jour" : "Publier le plat du jour"}
            </Button>
          </CardContent>
        </Card>

        {/* Current special preview */}
        {special && (
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 shadow-lg">
            <CardContent className="p-4">
              <Badge className="bg-amber-500 text-white mb-2">Plat du jour</Badge>
              <h3 className="text-lg font-bold">{special.name}</h3>
              {special.description && <p className="text-sm text-muted-foreground mt-1">{special.description}</p>}
              {special.price && <p className="text-xl font-bold text-amber-600 mt-2">{special.price.toFixed(2)}€</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
